import React, { useState, useEffect } from 'react';
import { apiFetch } from '../utils/api';
import { toast } from '../components/Toast';

const Notifications = () => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    
    const [vehicles, setVehicles] = useState([]);
    const [form, setForm] = useState({ message: '', channel: 'push', target: 'all', bus_codes: [] });
    const [submitting, setSubmitting] = useState(false);

    const loadData = async () => {
        setLoading(true);
        try {
            const [nRes, vRes] = await Promise.all([
                apiFetch('/notifications'),
                apiFetch('/vehicles'),
            ]);
            setNotifications(nRes.data || []);
            // Only active, non-deleted vehicles with a vehicle_code
            setVehicles((vRes.data || []).filter(v => v.is_active && v.vehicle_code));
        } catch(e) {
            toast.error("Failed to load notifications");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, []);

    const toggleBusCode = (code) => {
        setForm(prev => ({
            ...prev,
            bus_codes: prev.bus_codes.includes(code)
                ? prev.bus_codes.filter(c => c !== code)
                : [...prev.bus_codes, code],
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (form.target === 'specific_bus' && form.bus_codes.length === 0) {
            toast.error('Please select at least one bus code');
            return;
        }
        setSubmitting(true);
        try {
            await apiFetch('/notifications/broadcast', { method: 'POST', body: JSON.stringify(form) });
            toast.success("Broadcast sent successfully!");
            setIsModalOpen(false);
            setForm({ message: '', channel: 'push', target: 'all', bus_codes: [] });
            loadData();
        } catch(e) {
            toast.error(e.message || "Failed to broadcast message");
        } finally {
            setSubmitting(false);
        }
    };

    const getStatusColor = (status) => {
        if(status === 'sent') return 'var(--success)';
        if(status === 'failed') return 'var(--danger)';
        return 'var(--accent-blue)';
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <h1 className="page-title text-gradient" style={{ margin: 0 }}>Notification Center</h1>
                <button className="btn-primary" onClick={() => setIsModalOpen(true)} style={{ height: "40px" }}>+ Create Broadcast</button>
            </div>

            <div className="data-table-container glass-panel" style={{ display: 'flex', flexDirection: 'column' }}>
                <table className="data-table" style={{ flex: 1, fontSize: '14px' }}>
                    <thead>
                        <tr style={{ height: '48px' }}>
                            <th style={{ padding: '10px 16px' }}>Time</th>
                            <th style={{ padding: '10px 16px' }}>Type</th>
                            <th style={{ padding: '10px 16px' }}>Message Payload</th>
                            <th style={{ padding: '10px 16px' }}>Channel</th>
                            <th style={{ padding: '10px 16px' }}>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? <tr style={{ height: '48px' }}><td colSpan="5" style={{ textAlign:'center', padding: '10px 16px' }}>Loading...</td></tr> : 
                         notifications.length === 0 ? <tr style={{ height: '48px' }}><td colSpan="5" style={{textAlign:'center', color:'var(--text-muted)', padding: '10px 16px'}}>No notifications sent yet</td></tr> : 
                         notifications.map(n => (
                            <tr key={n.notif_id} style={{ height: '48px' }}>
                                <td style={{ fontSize:'0.8rem', color:'var(--text-muted)', padding: '10px 16px' }}>{new Date(n.created_at).toLocaleString()}</td>
                                <td style={{ padding: '10px 16px' }}><span className="badge badge-active" style={{ textTransform:'capitalize' }}>{n.type}</span></td>
                                <td style={{ maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: '10px 16px' }}>{n.message}</td>
                                <td style={{ textTransform: 'uppercase', fontSize: '0.8rem', padding: '10px 16px' }}>{n.channel}</td>
                                <td style={{ fontWeight:'bold', color: getStatusColor(n.status), textTransform:'uppercase', fontSize:'0.8rem', padding: '10px 16px' }}>{n.status}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2 className="modal-title">Broadcast Alert</h2>
                            <button className="modal-close" onClick={() => setIsModalOpen(false)}>&times;</button>
                        </div>
                        
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label className="form-label">Message Payload</label>
                                <textarea className="form-control" rows="4" required value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} placeholder="Enter broadcast message to parents and staff..." />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label className="form-label">Delivery Channel</label>
                                    <select className="form-control" value={form.channel} onChange={(e) => setForm({...form, channel: e.target.value})}>
                                        <option value="push">App Push Notification</option>
                                        <option value="sms">SMS Text</option>
                                        <option value="both">Both Push & SMS</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label className="form-label">Target Audience</label>
                                    <select className="form-control" value={form.target} onChange={(e) => setForm({...form, target: e.target.value, bus_codes: []})}>
                                        <option value="all">All Parents</option>
                                        <option value="specific_bus">Specific Bus(es)</option>
                                    </select>
                                </div>
                            </div>

                            {form.target === 'specific_bus' && (
                                <div className="form-group">
                                    <label className="form-label">Select Bus Code(s)</label>
                                    <div style={{
                                        border: '1px solid var(--border-color)',
                                        borderRadius: '8px',
                                        padding: '12px',
                                        maxHeight: '200px',
                                        overflowY: 'auto',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '8px',
                                        background: 'var(--bg-main)',
                                    }}>
                                        {vehicles.length === 0 ? (
                                            <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>No active vehicles found</span>
                                        ) : vehicles.map(v => {
                                            const isChecked = form.bus_codes.includes(v.vehicle_code);
                                            return (
                                                <label key={v.vehicle_id} style={{
                                                    display: 'flex', alignItems: 'center', gap: '10px',
                                                    cursor: 'pointer', fontSize: '14px',
                                                    padding: '6px 10px', borderRadius: '6px',
                                                    background: isChecked ? 'rgba(139,90,43,0.08)' : 'transparent',
                                                    border: `1px solid ${isChecked ? 'rgba(139,90,43,0.3)' : 'transparent'}`,
                                                    transition: 'all 0.12s',
                                                    userSelect: 'none',
                                                }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isChecked}
                                                        onChange={() => toggleBusCode(v.vehicle_code)}
                                                        style={{ width: '15px', height: '15px', accentColor: '#8B5E1A', cursor: 'pointer' }}
                                                    />
                                                    <span style={{ fontWeight: 600 }}>{v.vehicle_code}</span>
                                                    {v.registration_no && (
                                                        <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>— {v.registration_no}</span>
                                                    )}
                                                </label>
                                            );
                                        })}
                                    </div>
                                    {form.bus_codes.length > 0 && (
                                        <p style={{ fontSize: '12px', color: '#8B5E1A', marginTop: '6px' }}>
                                            {form.bus_codes.length} bus{form.bus_codes.length > 1 ? 'es' : ''} selected: {form.bus_codes.join(', ')}
                                        </p>
                                    )}
                                </div>
                            )}

                            <button type="submit" className="btn-primary btn-block" disabled={submitting}>
                                {submitting ? 'Pushing Out...' : 'Send Broadcast'}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Notifications;
